<script setup lang="ts">
    import BaseInput from '@/components/BaseInput.vue';
    import BaseButton from '@/components/BaseButton.vue';
    import ClientLogo from '@/components/ClientLogo.vue';
    import LocaleToggle from '@/components/LocaleToggle.vue';
    import { postLogin } from '@/api/auth.api';
    import { useAuthStore } from '@/store/auth.store';
    import { loginSchema } from '@/services/validators/login';
    import { ValidationError } from 'yup';
    import { useRouter } from 'vue-router';
    import { useI18n } from 'vue-i18n';
    import { ref } from 'vue';

    const router = useRouter();
    const { t } = useI18n();
    const authStore = useAuthStore();

    const username = ref('');
    const password = ref('');
    const error = ref<string | undefined>();
    const isPending = ref(false);

    async function onSubmit() {
        error.value = undefined;

        try {
            await loginSchema.validate({ username: username.value, password: password.value });
        } catch (validationError) {
            if (validationError instanceof ValidationError) {
                error.value = validationError.message;
            }
            return;
        }

        isPending.value = true;
        try {
            await postLogin(username.value, password.value);
            authStore.setAuthenticated(true);
            await router.push('/');
        } catch {
            error.value = t('login_error');
        } finally {
            isPending.value = false;
        }
    }
</script>

<template>
    <div
        class="relative flex h-full w-full flex-col items-center justify-center gap-6"
        style="background: linear-gradient(166.4deg, #fff 8.5%, #f5f5f7 50%, #fff 91.5%)"
    >
        <div class="absolute right-4 top-4">
            <LocaleToggle />
        </div>

        <div class="mb-4">
            <ClientLogo size="lg" />
        </div>

        <div class="flex flex-col items-center gap-3 px-4 text-center">
            <h1 class="font-input-serif text-2xl font-bold text-[#222] sm:text-4xl">
                {{ t('login_title') }}
            </h1>
            <p class="text-sm text-[#82828c]">{{ t('login_subtitle') }}</p>
        </div>

        <form
            class="mt-6 w-full max-w-sm px-4"
            @submit.prevent="onSubmit"
        >
            <div class="flex flex-col gap-4">
                <BaseInput
                    v-model="username"
                    type="text"
                    name="username"
                    :label="t('login_username_label')"
                    :placeholder="t('login_username_placeholder')"
                    :disabled="isPending"
                    data-test="username"
                    autocomplete="username"
                />

                <BaseInput
                    v-model="password"
                    type="password"
                    name="password"
                    :label="t('login_password_label')"
                    :placeholder="t('login_password_placeholder')"
                    :disabled="isPending"
                    data-test="password"
                    autocomplete="current-password"
                />

                <p
                    v-if="error !== undefined"
                    class="text-sm text-red-500"
                    data-test="error"
                >
                    {{ error }}
                </p>

                <BaseButton
                    submit
                    variant="filled"
                    :disabled="isPending"
                    data-test="submit"
                >
                    {{ isPending ? t('login_submitting') : t('login_submit') }}
                </BaseButton>
            </div>
        </form>
    </div>
</template>
